import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Users, Share2, Flame } from "lucide-react";
import ShareDialog, { nativeShare } from "./ShareDialog";
import { posStyle } from "./CardImagePositioner";
import { countryName } from "@/lib/utils_gg";
import { useApp } from "@/context/AppContext";

export const OnFire = ({ event, className = "w-3.5 h-3.5" }) => {
  const { onFireQuorum } = useApp();
  if (event.public_status === "cancelled" || (event.going_count || 0) < onFireQuorum) return null;
  return <Flame className={`${className} shrink-0 ml-0.5 text-primary`} fill="currentColor" strokeWidth={1.5} aria-label="on fire" data-testid={`on-fire-${event.slug}`} />;
};

const WD = { en: ["SUN","MON","TUE","WED","THU","FRI","SAT"], de: ["SO","MO","DI","MI","DO","FR","SA"] };
const MO = { en: ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"], de: ["JAN","FEB","MÄR","APR","MAI","JUN","JUL","AUG","SEP","OKT","NOV","DEZ"] };

export function shortDate(iso, lang) {
  const d = new Date(iso + "T00:00:00");
  return `${WD[lang]?.[d.getDay()] || WD.en[d.getDay()]} ${String(d.getDate()).padStart(2, "0")} ${(MO[lang] || MO.en)[d.getMonth()]}`;
}

export default function EventCard({ event, compact = false, hideFeatured = false }) {
  const { t, lang } = useApp();
  const status = ["cancelled", "postponed", "sold_out"].includes(event.public_status) ? event.public_status : null;
  const dateLabel = event.public_status === "postponed" && event.new_date_tba ? t("newDateTba") : event.start_date === event.end_date ? shortDate(event.start_date, lang) : `${shortDate(event.start_date, lang)} – ${shortDate(event.end_date, lang)}`;
  const fromSchedule = (event.schedule || []).flatMap(x => (x.lineup || "").split(",")).map(x => x.trim()).filter(Boolean);
  const people = [...new Set([...(event.djs || []), ...(event.artists || []), ...(event.live_acts || []), ...fromSchedule])];
  const horizontal = !compact;
  const TYPE_COLORS = { allnighter: "gg-type-allnighter", alldayer: "gg-type-alldayer", weekender: "bg-primary text-primary-foreground", live_show: "gg-type-live_show", record_fair: "gg-type-record_fair" };
  const lineRef = useRef(null);
  const [budget, setBudget] = useState(28);
  useEffect(() => {
    const el = lineRef.current; if (!el) return;
    const calc = () => setBudget(Math.max(12, Math.floor((el.clientWidth - 40) / 7.2)));
    calc(); const ro = new ResizeObserver(calc); ro.observe(el); return () => ro.disconnect();
  }, []);
  let used = 0; const shown = [];
  for (const n of people) { if (shown.length && used + n.length + 3 > budget) break; shown.push(n); used += n.length + 3; }
  const rest = people.length - shown.length;
  const [shareOpen, setShareOpen] = useState(false);
  const share = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!(await nativeShare(event))) setShareOpen(true);
  };

  return (
    <Link to={`/events/${event.slug}`} data-testid={`event-card-${event.slug}`}
      className={`group gg-card transition-colors duration-200 h-full ${event.is_featured ? "border-l-4 border-l-[#B7BFA6] dark:border-l-[#4A5240]" : "hover:border-primary"} ${status === "cancelled" ? "gg-card-cancelled" : ""} ${horizontal ? "flex flex-col md:grid md:grid-cols-[220px_1fr]" : "flex flex-col"}`}>
      <div className={`relative bg-muted overflow-hidden border-b border-foreground ${horizontal ? "aspect-[16/9] md:aspect-auto md:h-full md:min-h-[150px] md:border-b-0 md:border-r" : "aspect-[4/3]"}`}>
        <img src={event.main_image} alt={event.name} loading="lazy" style={posStyle(event.image_position)} className="gg-flyer absolute inset-0 group-hover:scale-[1.02] transition-transform duration-300" />
        {status && status !== "sold_out" && (
          <span className={`absolute top-2 right-2 eyebrow !text-[10px] px-2 py-1 gg-status-${status}`} data-testid={`status-badge-${event.slug}`}>{t(`public.${status}`)}</span>
        )}
        {event.is_featured && !hideFeatured && (
          <span data-testid={`featured-badge-${event.slug}`} className="absolute top-2 left-2 flex items-center gap-1 gg-recommends text-foreground eyebrow !text-[10px] px-2 py-1">
            <Star className="w-3 h-3" fill="currentColor" /> {t("featured")}
          </span>
        )}
      </div>
      <div className="relative p-4 md:p-5 pb-0 flex flex-col min-w-0 flex-1 md:h-full">
        <div className="flex-1 pb-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-display text-primary font-semibold tracking-wide text-sm md:text-base" data-testid={`card-date-${event.slug}`}>{dateLabel}</span>
        <div className="ml-auto flex items-center gap-1 shrink-0" data-testid={`card-status-area-${event.slug}`}>
          {status === "sold_out" && <span className="eyebrow !text-[9px] px-1.5 py-[5px] border border-foreground bg-background" data-testid={`status-badge-${event.slug}`}>{t("public.sold_out")}</span>}
          <span className={`eyebrow !text-[10px] px-2 py-1 ${TYPE_COLORS[event.event_type] || "bg-muted"}`} data-testid={`card-type-${event.slug}`}>{t(`types.${event.event_type}`)}</span>
        </div>
          {event.distance_km != null && <span className="eyebrow text-muted-foreground" data-testid={`distance-${event.slug}`}>{t("kmAway", { km: event.distance_km })}</span>}
        </div>
        <h3 className="mt-1 font-display uppercase text-xl md:text-2xl leading-tight tracking-tight line-clamp-2">{event.name}</h3>
        <div className="mt-1 text-sm text-muted-foreground">{event.city}, {countryName(event.country, lang)}</div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {event.genres.slice(0, 3).map(g => <span key={g} className="gg-chip !text-[10px]">{t(`genres.${g}`)}</span>)}
        </div>
        {people.length > 0 && (
          <div ref={lineRef} className="mt-2.5 text-xs md:text-sm whitespace-nowrap overflow-hidden" data-testid={`card-people-${event.slug}`}>
            <span className="eyebrow !text-[10px] mr-1.5">{t("djs")}</span>{shown.join(" · ")}
            {rest > 0 && <span className="text-muted-foreground ml-1.5" data-testid={`card-people-more-${event.slug}`}>+{rest}</span>}
          </div>
        )}
        </div>
        <div className="mt-auto pt-2 border-t border-foreground/20 flex items-center justify-between min-h-[40px]" data-testid={`card-footer-${event.slug}`}>
          {status !== "cancelled" && <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`card-going-${event.slug}`}><Users className="w-3.5 h-3.5" />{t("goingCount", { n: event.going_count || 0 })}<OnFire event={event} /></span>}
          <button type="button" onClick={share} aria-label={t("share")} className="p-1 -mr-1 text-foreground hover:text-primary transition-colors" data-testid={`card-share-${event.slug}`}><Share2 className="w-4 h-4" /></button>
        </div>
      </div>
      {shareOpen && <ShareDialog event={event} onClose={() => setShareOpen(false)} />}
    </Link>
  );
}
